import { Skeleton } from "antd"
import React, { useEffect, useRef } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { markProjectNotificationsSeen } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import { selectPrefsLoaded } from "../../User/redux/ui-preferences.selectors.ts"
import ProjectsGridPage from "../ProjectsGridPage/ProjectsGridPage.tsx"
import {
  isGridFilteredLink,
  type ProjectsView,
  useProjectsView
} from "../helpers/projects-view-preference.ts"

/**
 * Stands at the Projects entry URL and hands over to the view the user last chose, so
 * the sidebar, the dashboard tiles and post-save navigation all land there unchanged.
 */
export function ProjectsViewResolver() {
  const loaded = useAppSelector(selectPrefsLoaded)
  const [view] = useProjectsView()
  const { search } = useLocation()
  const resolved = useRef<ProjectsView | null>(null)
  const dispatch = useAppDispatch()

  // Visiting Projects is this app's only "seen" mechanism for the New
  // Project badge (see docs/kb/decisions/2026-09-12-notification-badges-phase-1.md)
  // — it clears whichever of the resolved list/grid view the user lands on.
  useEffect(() => {
    dispatch(markProjectNotificationsSeen())
  }, [dispatch])

  // A KPI tile drills to a server-supplied `/projects/?project_status=…`;
  // honouring the preference there would drop the filter silently.
  if (isGridFilteredLink(search)) return <ProjectsGridPage />

  if (!loaded) return <Skeleton active className={"p-4"} />

  // Resolved once. A later change to the stored value — a failed save rolling back,
  // or a slow response landing — must not navigate a user who is already reading.
  if (resolved.current === null) resolved.current = view

  if (resolved.current === "list") {
    // Replaced, not pushed: a push would leave the entry URL one step back and
    // redirect forward again the moment the user pressed Back.
    return (
      <Navigate to={{ pathname: RoutesPaths.project.list, search }} replace />
    )
  }

  return <ProjectsGridPage />
}
